import {Button, Divider, Group, Tooltip} from '@mantine/core';
import React from 'react';
import type {ActionVariant} from 'src/design/atoms/actionConfig';
import {getActionConfig} from 'src/design/atoms/actionConfig';

import classes from './TableToolbar.module.css';

/**
 * Compositional table-header toolbar. `TableToolbar.Filters` injects `size: 'xs'` into
 * children without an explicit `size`, aligning filter controls with `xs` action buttons.
 * @registry Filters + actions toolbar for table `renderTopToolbarCustomActions`.
 */

// --- Sub-component Props ---

export interface TableToolbarProps {
  children: React.ReactNode;
}

export interface TableToolbarFiltersProps {
  children: React.ReactNode;
}

export interface TableToolbarActionsProps {
  children: React.ReactNode;
}

export type TableToolbarActionVariant = ActionVariant;

export interface TableToolbarActionProps {
  /** Button label - optional for icon-only buttons */
  children?: React.ReactNode;
  /** Custom icon (overrides variant icon) */
  icon?: React.ReactNode;
  onClick?: () => void;
  /** Visual variant - semantic variants include built-in icons */
  variant?: TableToolbarActionVariant;
  loading?: boolean;
  disabled?: boolean;
  /** Tooltip shown on hover — most useful to explain why the action is disabled */
  tooltip?: string;
  /** Polymorphic element (e.g. `component={Link} to="/path"`) so navigation renders a real link, not a button+navigate. Mirrors `PageHeader.Action`. */
  component?: React.ElementType;
  /** Destination when `component` is a routing `Link`. */
  to?: string;
}

// --- Sub-components ---

const TableToolbarFilters: React.FC<TableToolbarFiltersProps> = ({
  children,
}) => (
  <Group
    gap="md"
    align="center"
    wrap="wrap"
    w={{base: '100%', sm: 'auto'}}
    className={classes.filters}>
    {React.Children.map(children, child => {
      if (!React.isValidElement(child)) {
        return child;
      }
      const props = child.props as {size?: unknown};
      if (props.size !== undefined) {
        return child;
      }
      return React.cloneElement(child as React.ReactElement<{size?: string}>, {
        size: 'xs',
      });
    })}
  </Group>
);
TableToolbarFilters.displayName = 'TableToolbar.Filters';

const TableToolbarActions: React.FC<TableToolbarActionsProps> = ({
  children,
}) => <Group gap="xs">{children}</Group>;
TableToolbarActions.displayName = 'TableToolbar.Actions';

/** Standardized action button for TableToolbar */
const TableToolbarAction: React.FC<TableToolbarActionProps> = ({
  children,
  icon,
  onClick,
  variant = 'primary',
  loading = false,
  disabled = false,
  tooltip,
  component,
  to,
}) => {
  const config = getActionConfig(variant);
  const displayIcon = icon ?? config.icon;
  // Generic React.ElementType doesn't satisfy Mantine's component-keyed
  // overload union; cast the passthrough props (same shape as PageHeader.Action).
  const polymorphicProps = {component, to} as Record<string, unknown>;

  const button = (
    <Button
      {...polymorphicProps}
      size="xs"
      leftSection={displayIcon}
      onClick={onClick}
      loading={loading}
      disabled={disabled}
      variant={config.buttonVariant}
      color={config.color}>
      {children ?? config.text}
    </Button>
  );

  if (!tooltip) {
    return button;
  }

  return (
    <Tooltip label={tooltip} withArrow multiline maw={280}>
      <span>{button}</span>
    </Tooltip>
  );
};
TableToolbarAction.displayName = 'TableToolbar.Action';

// --- Main Component ---

const TableToolbarRoot: React.FC<TableToolbarProps> = ({children}) => {
  const childArray = React.Children.toArray(children);

  // Use displayName for reliable component identification (works in production builds)
  const getDisplayName = (child: React.ReactNode): string | undefined => {
    if (React.isValidElement(child) && typeof child.type !== 'string') {
      return (child.type as React.FC & {displayName?: string}).displayName;
    }
    return undefined;
  };

  const filtersChild = childArray.find(
    child => getDisplayName(child) === 'TableToolbar.Filters'
  );
  const actionsChild = childArray.find(
    child => getDisplayName(child) === 'TableToolbar.Actions'
  );

  const showDivider = filtersChild && actionsChild;

  return (
    <Group justify="space-between" gap="md" wrap="wrap" className={classes.toolbar}>
      {actionsChild}
      {showDivider && <Divider orientation="vertical" visibleFrom="sm" />}
      {filtersChild}
    </Group>
  );
};
TableToolbarRoot.displayName = 'TableToolbar';

// --- Compound Component Export ---

export const TableToolbar = Object.assign(TableToolbarRoot, {
  Filters: TableToolbarFilters,
  Actions: TableToolbarActions,
  Action: TableToolbarAction,
});

// Also export individual components for flexibility
export {TableToolbarAction, TableToolbarActions, TableToolbarFilters};
